import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import { setActiveAircraft, clearActiveAircraft } from 'actions';
import { AircraftFactory, FlightFactory } from 'classes';
import {
  AIRCRAFT_TYPE_OPTIONS_W_ALL,
  FLIGHT_GET_API_REQUEST_PAGE_SIZE,
  NOT_AVAILABLE,
  SELECT_OPTION_ALL,
} from 'constants';
import { getUTCDateTime, subtractTimeFromNow } from 'utils';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Button } from 'react-bootstrap';
import ButtonItem from 'components/inputs/ButtonItem';
import Select from 'components/inputs/Select';
import ShipSelect from 'components/default/ShipSelect';
import SkyPulseTable from 'components/SkyPulseTable';
import PropTypes from 'prop-types';
import styles from './styles.module.css';

const idPrefix = 'FlightSelect';

const defaultFilterType = 'fleet';

const columns = [
  {
    name: 'Departure UTC',
    key: 'utc',
    sortable: true,
    className: styles.utcColumn,
    size: 'xsmall',
    dataCyClassName: 'FlightSelect-FlightsTableColumn-DepartureUtc',
  },
  {
    name: 'Tail',
    key: 'tail',
    sortable: true,
    size: 'xsmall',
    dataCyClassName: 'FlightSelect-FlightsTableColumn-Tail',
  },
  {
    name: 'Flight',
    key: 'number',
    sortable: true,
    size: 'xsmall',
    dataCyClassName: 'FlightSelect-FlightsTableColumn-Flight',
  },
  {
    name: 'Depart',
    key: 'dep',
    sortable: true,
    size: 'xsmall',
    dataCyClassName: 'FlightSelect-FlightsTableColumn-Depart',
  },
  {
    name: 'Arrive',
    key: 'des',
    sortable: true,
    size: 'xsmall',
    dataCyClassName: 'FlightSelect-FlightsTableColumn-Arrive',
  },
  {
    name: 'Status',
    key: 'status',
    sortable: true,
    size: 'xsmall',
    dataCyClassName: 'FlightSelect-FlightsTableColumn-Status',
  },
];

const FlightSelect = (props) => {
  const {
    rActiveAircraft,
    rFlightMap,
    aircraftFilters,
    fleetFilter,
    onCancel,
    onFleetFilterChange,
    onSelect,
    setActiveAircraft,
    clearActiveAircraft,
  } = props;

  const navigate = useNavigate();
  const [filterType, setFilterType] = useState(
    rActiveAircraft ? 'tail' : defaultFilterType,
  );
  const [skypulseTableFlightData, setSkypulseTableFlightData] = useState([]);
  const [showShipSelect, setShowShipSelect] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [allFlights, setAllFlights] = useState(rFlightMap);
  const [showFetchMoreButton, setShowFetchMoreButton] = useState(true);
  const [page, setPage] = useState(1);

  const tail = rActiveAircraft ? rActiveAircraft.tail : undefined;

  useEffect(() => {
    const updateList = async () => {
      setSkypulseTableFlightData([]);
      let currentFlightList = Array.from(allFlights.values());

      const dt7DaysAgo = subtractTimeFromNow(7, 'days');
      const dt30DaysAgo = subtractTimeFromNow(30, 'days');
      const flights = currentFlightList
        .filter(
          (flight) => flight.modified > (page > 1 ? dt30DaysAgo : dt7DaysAgo),
        )
        .reduce((arr, flight) => {
          const aircraft = AircraftFactory.getSummaryAircraftById(
            flight.aircraft_id,
          );
          if (
            (filterType === 'fleet' &&
              (fleetFilter === SELECT_OPTION_ALL.value ||
                (aircraft && aircraft.aircraft_type.id === fleetFilter))) ||
            (filterType === 'tail' && aircraft && aircraft.tail === tail)
          ) {
            arr.push({
              id: flight.id,
              utc: {
                display: flight.departure_timestamp
                  ? getUTCDateTime(flight.departure_timestamp)
                  : NOT_AVAILABLE,
                sort: flight.departure_timestamp || 0,
              },
              tail: aircraft?.tail,
              number: flight.number,
              dep: flight.departure_airport?.value.iata,
              des: flight.destination_airport?.value.iata,
              status: flight.is_open ? 'Open' : 'Closed',
              aircraftId: aircraft?.id,
            });
          }
          return arr;
        }, []);
      setSkypulseTableFlightData(flights);
    };

    if (allFlights) {
      updateList();
    }
  }, [filterType, fleetFilter, tail, allFlights, page]);

  const handleFilterTypeChange = (e) => {
    const { value } = e.target;
    setFilterType(value);
  };

  const handleFleetSelectChange = ({ target: { value } }) => {
    setFilterType('fleet');
    onFleetFilterChange(value);
  };

  // if the current filter is tail and no active aircraft
  if ((filterType === 'tail' && !rActiveAircraft) || showShipSelect) {
    return (
      <ShipSelect
        fleetFilter={aircraftFilters?.aircraft_type || SELECT_OPTION_ALL.value}
        onCancel={() => {
          if (rActiveAircraft) {
            setShowShipSelect(false);
          } else {
            navigate(-1);
          }
        }}
        onFleetFilterChange={(fleet) => onFleetFilterChange(fleet)}
        onSelect={(aircraft) => {
          clearActiveAircraft();
          setActiveAircraft(aircraft);
          setShowShipSelect(false);
        }}
      />
    );
  }

  return (
    <Row data-cy-id="FlightSelect" className={styles.root} noGutters>
      <Col className={styles.body}>
        <Row className={styles.bodyHeaderRow} noGutters>
          <Col className="title_1">Flight selection</Col>
          <Col md="auto">
            <Button
              data-cy-id="FlightSelect-CancelButton"
              className="button_reg"
              onClick={onCancel}
            >
              Cancel
            </Button>
          </Col>
        </Row>
        <Row className={styles.fleetSelectionRow} noGutters>
          <Col md={2}>
            <ButtonItem
              label={
                <div className={styles.buttonLabel}>
                  Fleet: &nbsp;
                  <Select
                    data-cy-id="FlightSelect-FiltersPanel-FleetSelect"
                    idPrefix={idPrefix}
                    onChange={handleFleetSelectChange}
                    options={AIRCRAFT_TYPE_OPTIONS_W_ALL}
                    value={fleetFilter}
                  />
                </div>
              }
              value="fleet"
              onChange={handleFilterTypeChange}
              type="radio"
              data-cy-id="FlightSelect-FiltersPanel-FleetInput"
              classname={styles.buttonItemLabel}
              isChecked={filterType === 'fleet'}
              idPrefix={`${idPrefix}-fleet`}
            />
          </Col>
          <Col md={2}>
            {rActiveAircraft ? (
              <ButtonItem
                label={
                  <div className={styles.buttonLabel}>
                    Tail {tail} &nbsp;
                    <Button
                      data-cy-id="FlightSelect-FiltersPanel-ChangeTailButton"
                      className="button_reg"
                      onClick={() => {
                        setShowShipSelect(true);
                        setFilterType('tail');
                      }}
                    >
                      Change Tail
                    </Button>
                  </div>
                }
                data-cy-id="FlightSelect-FiltersPanel-TailInput"
                value="tail"
                onChange={handleFilterTypeChange}
                type="radio"
                classname={styles.buttonItemLabel}
                isChecked={filterType === 'tail'}
                idPrefix={`${idPrefix}-tail`}
              />
            ) : (
              <ButtonItem
                data-cy-id="FlightSelect-FiltersPanel-SelectTailInput"
                label="Select a tail"
                value="tail"
                onChange={handleFilterTypeChange}
                type="radio"
                classname={styles.buttonItemLabel}
                isChecked={filterType === 'tail'}
                idPrefix={`${idPrefix}-tail`}
              />
            )}
          </Col>
        </Row>
        <Row className={styles.flightListRow} noGutters>
          <Col md="auto" className={styles.tableContainer}>
            <SkyPulseTable
              title="Flights"
              columns={columns}
              rows={skypulseTableFlightData}
              defaultSort="utc"
              defaultSortState="descending"
              onRowClick={async (id) => {
                if (id === 'root') {
                  return;
                }
                let flight = allFlights.get(id);
                if (!flight) {
                  console.error('Flight not found');
                } else {
                  onSelect(flight);
                }
              }}
              showFetchMoreButton={showFetchMoreButton}
              loadingMoreRows={isLoading}
              fetchMoreData={async () => {
                let flightMap = allFlights;
                setIsLoading(true);
                const dt30DaysAgo = subtractTimeFromNow(30, 'days');
                const pageSize =
                  page > 1
                    ? FLIGHT_GET_API_REQUEST_PAGE_SIZE
                    : allFlights.size + 100;
                let { flightMap: flights, ct: flightCount } =
                  await FlightFactory.getFlights(
                    dt30DaysAgo,
                    page,
                    pageSize,
                    filterType === 'fleet' ? fleetFilter : null,
                    filterType === 'tail' ? rActiveAircraft?.id : null,
                    filterType !== 'tail',
                  );
                setShowFetchMoreButton(
                  page === 1 ? true : flightCount === pageSize,
                );
                setPage(page + 1);
                flightMap = new Map([...flightMap, ...flights]);
                setAllFlights(flightMap);
                setIsLoading(false);
              }}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

FlightSelect.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
};

const state = (state) => {
  return {
    rActiveAircraft: state.activeAircraft,
    rFlightMap: state.flightMap,
  };
};

const actions = {
  setActiveAircraft,
  clearActiveAircraft,
};

export default connect(state, actions)(FlightSelect);
